import { RequestFilters } from '@/schema'

/**
 * Number of concurrent uploads to Azure Blob Storage
 * Can be configured via VITE_UPLOAD_CONCURRENT_UPLOADS environment variable
 * Defaults to 200 if not set or invalid.
 */
const parseMaxConcurrentUploads = (): number => {
  const parsed = Number.parseInt(
    import.meta.env.VITE_UPLOAD_CONCURRENT_UPLOADS,
    10
  )
  return !Number.isNaN(parsed) && parsed > 0 ? parsed : 200
}

/**
 * Delay between uploads (in milliseconds) for better UX visibility
 * Can be configured via VITE_UPLOAD_DELAY_MS environment variable
 * Defaults to 2000ms (2 seconds) if not set or invalid
 */
const parseUploadDelayMs = (): number => {
  const parsed = Number.parseInt(import.meta.env.VITE_UPLOAD_DELAY_MS, 10)
  return !Number.isNaN(parsed) && parsed >= 0 ? parsed : 2000
}

/**
 * Size of the batch to confirm uploads
 * Can be configured via VITE_UPLOAD_CONFIRM_SIZE environment variable
 * Defaults to 1 if not set or invalid
 */
const parseUploadConfirmSize = (): number => {
  const parsed = Number.parseInt(import.meta.env.VITE_UPLOAD_CONFIRM_SIZE, 10)
  return !Number.isNaN(parsed) && parsed > 0 ? parsed : 1
}

export const MAX_CONCURRENT_UPLOADS = parseMaxConcurrentUploads()
export const UPLOAD_DELAY_MS = parseUploadDelayMs()
export const UPLOAD_CONFIRM_SIZE = parseUploadConfirmSize()

/**
 * Base URL for all API requests
 * Can be configured via VITE_API_BASE_URL environment variable
 *
 * In production: Uses full URL from environment variable or defaults to localhost
 *
 * Note: For development with external APIs, configure Vite proxy in vite.config.ts
 * to forward requests to the actual API server and avoid CORS issues.
 *
 * Mixed Content Solution: When the page is served over HTTPS and API is HTTP,
 * use relative paths so requests go through the same domain (e.g., Azure Front Door),
 * which can then proxy to the HTTP backend. This avoids mixed content errors.
 */
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_BASE_URL
  const isDev = import.meta.env.DEV

  // If no env URL is set, use relative path for dev (Vite proxy) or default localhost
  if (!envUrl) {
    // In development, use relative path to leverage Vite proxy
    // In production build, use default localhost
    return isDev ? '' : 'http://localhost:3001'
  }

  // If env URL is set and it's an absolute URL
  if (envUrl.startsWith('http')) {
    // In production, check if we're on HTTPS page with HTTP API (mixed content scenario)
    // Use relative paths to route through the same domain (reverse proxy)
    // This allows Azure Front Door or similar to proxy HTTPS->HTTP backend
    if (
      typeof globalThis !== 'undefined' &&
      globalThis.location.protocol === 'https:' &&
      envUrl.startsWith('http://')
    ) {
      // Use relative paths - requests will go to same domain (HTTPS)
      // Configure your reverse proxy (Azure Front Door) to forward /api and /identity
      // to the HTTP backend to avoid mixed content errors
      return ''
    }

    // If API is already HTTPS, or page is HTTP, use the API URL directly
    return envUrl
  }

  // If env URL is a relative path or other format, use it as-is
  return envUrl
}

export const API_BASE_URL = getApiBaseUrl()
export const API_BASE_URL_MSW = import.meta.env.VITE_ENABLE_MSW
  ? 'http://localhost:3001'
  : ''

export const STATUS_CODES = {
  SUCCESS: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_CONTENT: 422,
  INTERNAL_SERVER_ERROR: 500,
}

export const HTTP_STATUS_OK_MIN = 200
export const HTTP_STATUS_OK_MAX = 300
export const UPLOAD_PROGRESS_COMPLETE = 100

export const AZURE_BLOB_TYPE_HEADER = 'x-ms-blob-type'
export const AZURE_BLOCK_BLOB = 'BlockBlob'
export const CONTENT_TYPE_HEADER = 'Content-Type'

export const SEARCH_DEBOUNCE_DELAY_MS = 500
export const MIN_SEARCH_LENGTH = 3
export const DEFAULT_MAX_CHARACTERS = 32000
/**
 * Default page size for table pagination
 * Can be configured via VITE_TABLE_PAGE_SIZE environment variable
 * Defaults to 20 if not set
 */
const parseTablePageSize = (): number => {
  const parsed = Number.parseInt(import.meta.env.VITE_TABLE_PAGE_SIZE, 10)
  return Number.isNaN(parsed) ? 20 : parsed
}

/**
 * Batch size for file upload requests to /request API
 * Can be configured via VITE_UPLOAD_FILE_BATCH_SIZE environment variable
 * Defaults to 20 if not set
 */
const parseUploadFileBatchSize = (): number => {
  const parsed = Number.parseInt(
    import.meta.env.VITE_UPLOAD_FILE_BATCH_SIZE,
    10
  )
  return !Number.isNaN(parsed) && parsed > 0 ? parsed : 20
}

export const DEFAULT_PAGE = 0
export const TABLE_PAGE_SIZE = parseTablePageSize()
export const UPLOAD_FILE_BATCH_SIZE = parseUploadFileBatchSize()

export const DEFAULT_TABLE_SORT_BY = '+created_at'
export const DEFAULT_GET_ALL_LIMIT = 1000
export const THREAD_AUTO_REFRESH_DELAY_MS = 600000 // 10 minutes
export const DEFAULT_STALE_TIME_MS = 30 * 1000 // 30 seconds

export const WEBPUBSUB_TOKEN_REFRESH_BEFORE_EXPIRY_MS = 5 * 10 * 60 * 1000 // 50 minutes

/**
 * RFC 5322 compliant email validation regex (simplified)
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Default values for table pagination parameters.
 * These defaults are used when no specific values are provided,
 * ensuring consistent behavior across all table-based API requests.
 *
 * Default behavior:
 * - No filters applied (empty filter_by object)
 * - Page size from TABLE_PAGE_SIZE constant (defaults to 20, configurable via env)
 * - Start from the beginning (offset 0)
 * - No search query (empty string)
 * - Sort by creation date in ascending order (oldest first)
 *
 * @see RequestFilters for detailed field descriptions
 * @see TABLE_PAGE_SIZE for the default limit value
 * @see DEFAULT_TABLE_SORT_BY for the default sort by value
 *
 * @example
 * ```typescript
 * // Use defaults directly
 * const params = DEFAULT_TABLE_PAGINATION_PARAMS
 *
 * // Override specific values using spread operator
 * const customParams = {
 *   ...DEFAULT_TABLE_PAGINATION_PARAMS,
 *   limit: 50,
 *   sort_by: '-name'
 * }
 *
 * // Or use mergeTablePaginationParams utility function
 * const mergedParams = mergeTablePaginationParams({ limit: 50, sort_by: '-name' })
 * ```
 */
export const DEFAULT_SEARCH_REQUEST_BODY: RequestFilters = {
  filter_by: {},
  limit: TABLE_PAGE_SIZE,
  offset: 0,
  q: '',
  sort_by: DEFAULT_TABLE_SORT_BY,
}
export const DATE_FORMAT = 'MMM DD, YYYY'
export const DATETIME_FORMAT = 'MMM DD, YYYY h:mm A'
export const DATE_PICKER_FORMAT = 'MM/DD/YYYY'
export const DEFAULT_AUTO_CLOSE_TOAST_MS = 3000
export const MAX_TEXT_LENGTH = 255

export const ERROR_CODES = {
  DUPLICATE: 'RES_3002',
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum EArchiveExtension {
  ZIP = '.zip',
  SEVEN_ZIP = '.7z',
}

export enum EDocumentExtension {
  HTML = '.html',
  HTM = '.htm',
  PDF = '.pdf',
}

export enum EDocumentType {
  HTML = 'html',
  PDF = 'pdf',
}

export const ALLOWED_DOCUMENT_EXTENSIONS = [
  EDocumentExtension.HTML,
  EDocumentExtension.HTM,
  EDocumentExtension.PDF,
]

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000'
